import { looks as rules } from "@app/domain";
import { DECK_IDS, type Look, type LookKind, SCENE_IDS } from "@app/protocol";
import { DECKS, type Deck, SCENES } from "@app/ui";
import { useEffect, useId } from "react";
import { useT } from "../i18n/context";
import { LookMarks } from "./LookMarks";
import { pickStep } from "./lookPick";
import type { LooksState } from "./useLooks";
import { useOnce } from "./useOnce";

const deckOf = (id: string): Deck | undefined => (DECKS as Record<string, Deck>)[id];

/** What each kind shows: its ids in order, its picture, its "New" mark, its card shape. */
const KINDS = {
  scene: {
    ids: SCENE_IDS,
    image: (id: string) => SCENES[id as keyof typeof SCENES].thumb,
    fresh: (id: string) => SCENES[id as keyof typeof SCENES].fresh === true,
    aspect: "aspect-video",
    columns: "repeat(auto-fill,minmax(150px,1fr))",
  },
  deck: {
    ids: DECK_IDS,
    image: (id: string) => deckOf(id)?.preview ?? "",
    fresh: (id: string) => deckOf(id)?.fresh === true,
    aspect: "aspect-[640/313]",
    columns: "repeat(auto-fill,minmax(220px,1fr))",
  },
} as const;

function useLookName(): (look: Look) => string {
  const { t } = useT();
  return (look) => (look.kind === "scene" ? t(`scene.${look.id}`) : t(`deck.${look.id}`));
}

function LookCard({
  look,
  selected,
  state,
  onPick,
}: {
  look: Look;
  selected: boolean;
  state: LooksState;
  onPick: () => void;
}) {
  const { t } = useT();
  const name = useLookName();
  const ribbonId = useId();
  const access = state.accessOf(look);
  const locked = access?.kind === "ended";
  const notified = state.notified(look);
  const starting = state.pending.has(rules.lookKey(look));
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-pressed={selected}
        aria-busy={starting}
        aria-label={name(look)}
        aria-describedby={
          rules.isClub(look) && access !== undefined && access.kind !== "club"
            ? ribbonId
            : undefined
        }
        className={`relative overflow-hidden rounded-[14px] border-2 text-start ${selected ? "border-gold shadow-glow-soft" : "border-ink/18"}`}
        onClick={onPick}
      >
        <img
          src={KINDS[look.kind].image(look.id)}
          alt=""
          loading="lazy"
          className={`block w-full object-cover ${KINDS[look.kind].aspect} ${locked ? "brightness-[0.55] grayscale-[0.4]" : ""}`}
        />
        <LookMarks
          look={look}
          access={access}
          skew={state.skew}
          ribbonId={ribbonId}
          isNew={KINDS[look.kind].fresh(look.id)}
          starting={starting}
        />
        <span
          className={`block px-3 py-2 text-[13px] ${selected ? "bg-gold font-extrabold text-gold-ink" : "bg-ink/6 font-bold"}`}
        >
          {name(look)}
        </span>
      </button>
      {locked && (
        <button
          type="button"
          disabled={notified}
          className="btn-glass h-9 rounded-pill px-4 text-[13px] font-bold disabled:opacity-70"
          onClick={() => void state.notify(look)}
        >
          {t(notified ? "club.notified" : "club.notify")}
        </button>
      )}
    </div>
  );
}

/**
 * The table's scene or deck (review 0012 S1, 0015; ADR 0029, 0030): the owner's choice, seen by
 * everyone at the table. Picking a Club look never tried starts its three-day trial; an ended trial
 * stays in view, locked, with "tell me".
 */
export function LookPicker({
  kind,
  selected,
  onChange,
  state,
}: {
  kind: LookKind;
  selected: string;
  onChange: (look: Look) => void;
  state: LooksState;
}) {
  const { t } = useT();
  const name = useLookName();
  const all = KINDS[kind].ids.map((id) => ({ kind, id }) as Look);
  const fallback = all[0] as Look;
  const current = all.find((look) => look.id === selected) ?? fallback;
  const currentAccess = state.accessOf(current);
  const ended = all.find((look) => state.accessOf(look)?.kind === "ended");
  const note = useOnce(
    `hcc:look-ended:${ended === undefined ? "" : rules.lookKey(ended)}`,
    ended !== undefined,
  );
  // A choice the player lost the right to (the trial ended) falls back to the default, listed first.
  useEffect(() => {
    if (currentAccess !== undefined && pickStep(currentAccess, false) !== "select")
      onChange(fallback);
  }, [currentAccess, onChange, fallback]);
  const pick = async (look: Look) => {
    const step = pickStep(state.accessOf(look), state.pending.has(rules.lookKey(look)));
    const picked = step === "select" || (step === "start-trial" && (await state.startTrial(look)));
    if (!picked) return;
    onChange(look);
    state.remember(look);
  };
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="sr-only">{t(`home.${kind}`)}</legend>
      <div className="flex flex-col gap-2.5 rounded-[16px] border-[1.5px] border-edge bg-panel-strong px-4 py-3.5 shadow-pill">
        <span
          aria-hidden="true"
          className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink/60"
        >
          {t(`home.${kind}`)}
        </span>
        {note && ended !== undefined && (
          <p role="status" className="m-0 text-[13px] text-ink/80">
            {t(`club.ended.${kind}`, { name: name(ended) })}
          </p>
        )}
        <div className="grid gap-3" style={{ gridTemplateColumns: KINDS[kind].columns }}>
          {all.map((look) => (
            <LookCard
              key={look.id}
              look={look}
              selected={look.id === current.id}
              state={state}
              onPick={() => void pick(look)}
            />
          ))}
        </div>
      </div>
    </fieldset>
  );
}
